import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Heart, Sparkles, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "../lib/api";
export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [transaction, setTransaction] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;

    const poll = async (attempts = 0) => {
      if (cancelled || attempts >= 8) {
        if (!cancelled) setStatus("failed");
        return;
      }
      try {
        const res = await axios.get(
          `${API}/payments/status/${sessionId}`
        );
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          setTransaction(res.data);
          // Trigger PWA install prompt — highest engagement moment
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("startip:payment-complete"));
          }, 1800);
          return;
        }
        if (res.data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch {
        if (!cancelled) setTimeout(() => poll(attempts + 1), 2000);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "checking" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <Loader2 className="w-16 h-16 text-neon-pink mx-auto animate-spin" />
            <h2
              className="font-heading font-bold text-2xl text-white"
              data-testid="status-checking"
            >
              Verificando pago...
            </h2>
            <p className="text-zinc-400 font-body">Espera un momento</p>
          </motion.div>
        )}

        {status === "paid" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="space-y-6"
          >
            <div className="relative inline-block">
              <CheckCircle className="w-20 h-20 text-neon-green mx-auto" />
              <Sparkles className="w-6 h-6 text-neon-pink absolute -top-1 -right-1 animate-bounce" />
            </div>
            <h2
              className="font-heading font-extrabold text-3xl text-white uppercase"
              data-testid="status-success"
            >
              Gracias!
            </h2>
            <p className="text-zinc-400 font-body text-lg">
              {transaction?.service_id ? (
                <>
                  Has contratado{" "}
                  <span className="text-neon-green font-bold">
                    {transaction?.model_name}
                  </span>
                  {" "}por{" "}
                  <span className="text-neon-green font-bold">
                    {transaction?.amount}€
                  </span>
                </>
              ) : (
                <>
                  Tu propina de{" "}
                  <span className="text-neon-green font-bold">
                    {transaction?.amount}€
                  </span>{" "}
                  para{" "}
                  <span className="text-neon-pink font-bold">
                    {transaction?.model_name}
                  </span>{" "}
                  se ha enviado correctamente.
                </>
              )}
            </p>
            <div className="flex items-center justify-center gap-2">
              <Heart className="w-5 h-5 text-neon-pink" fill="#FF007F" />
              <Heart className="w-4 h-4 text-neon-pink opacity-60" fill="#FF007F" />
              <Heart className="w-3 h-3 text-neon-pink opacity-30" fill="#FF007F" />
            </div>
            {transaction?.service_id && user ? (
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate("/my-services")}
                  data-testid="go-to-services-btn"
                  className="mt-4 bg-primary hover:bg-primary/90 text-white font-heading font-bold uppercase rounded-xl h-14 px-8 shadow-[4px_4px_0px_0px_rgba(139,92,246,0.25)] flex items-center gap-2 mx-auto">
                  <MessageCircle className="w-5 h-5" />
                  Ver mi servicio y chat
                </Button>
                <Link to="/" data-testid="back-home-link" className="text-xs text-zinc-500 hover:text-white font-body transition-colors">
                  Volver al inicio
                </Link>
              </div>
            ) : (
              <Link to="/" data-testid="back-home-link">
                <Button className="mt-4 bg-neon-pink hover:bg-neon-pink/90 text-white font-heading font-bold uppercase rounded-xl h-14 px-8 shadow-[4px_4px_0px_0px_rgba(255,0,127,0.25)]">
                  Volver al inicio
                </Button>
              </Link>
            )}
          </motion.div>
        )}

        {status === "failed" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h2
              className="font-heading font-bold text-2xl text-white"
              data-testid="status-failed"
            >
              Pago no completado
            </h2>
            <p className="text-zinc-400 font-body">
              El pago no se pudo verificar. Intentalo de nuevo.
            </p>
            <Link to="/" data-testid="retry-link">
              <Button className="mt-4 bg-zinc-800 hover:bg-zinc-700 text-white font-heading font-bold uppercase rounded-xl h-14 px-8">
                Volver al inicio
              </Button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
}
